/**
 * The .ics download. build-prompt section 9.
 *
 * Always visible, and the only calendar path in v1: the emailed invitation is
 * behind a feature flag and off, because it would mean accepting a guest's
 * email address. Generated in the browser, so nothing is sent anywhere.
 */

import type { PublicEvent } from './api.js';

/**
 * A stable, deterministic UID per (event, household).
 *
 * Nothing updates a calendar entry in v1, we hold no address to send an
 * update to, but a deterministic UID means a future version could update in
 * place rather than creating a duplicate.
 */
export function calendarUid(slug: string, householdToken: string): string {
  // The token is a capability; hashing is overkill for a local file, but a raw
  // invitation token inside a calendar entry the guest may share onwards is
  // worth avoiding.
  const short = householdToken.slice(0, 12);
  return `${slug}-${short}@presente.school`;
}

export function buildIcs(
  event: PublicEvent,
  options: { uid: string; now?: Date } ,
): string {
  const start = new Date(event.startsAt);
  const end = event.endsAt
    ? new Date(event.endsAt)
    : new Date(start.getTime() + 3 * 60 * 60 * 1000);

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Presente//PT-BR//',
    'CALSCALE:GREGORIAN',
    // METHOD:PUBLISH, not REQUEST: this is a file the guest imports herself,
    // with no organiser inviting anybody.
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${options.uid}`,
    `DTSTAMP:${utc(options.now ?? new Date())}`,
    `DTSTART:${utc(start)}`,
    `DTEND:${utc(end)}`,
    `SUMMARY:${escape(`Aniversário de ${event.childName}`)}`,
    `LOCATION:${escape(event.venue)}`,
    `DESCRIPTION:${escape(
      `${event.ageTurning} anos. Festa de ${event.childName}.`,
    )}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return lines.map(fold).join('\r\n') + '\r\n';
}

/** UTC form, which needs no VTIMEZONE block and cannot drift. */
function utc(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/** RFC 5545 escaping: commas, semicolons, backslashes and newlines. */
function escape(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/** Content lines must not exceed 75 octets; continuations start with a space. */
function fold(line: string): string {
  if (line.length <= 75) return line;

  const parts: string[] = [line.slice(0, 75)];
  let rest = line.slice(75);
  while (rest.length > 74) {
    parts.push(` ${rest.slice(0, 74)}`);
    rest = rest.slice(74);
  }
  if (rest.length > 0) parts.push(` ${rest}`);
  return parts.join('\r\n');
}

export function downloadIcs(event: PublicEvent, uid: string): void {
  const blob = new Blob([buildIcs(event, { uid })], {
    type: 'text/calendar;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `festa-${event.slug}.ics`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(url);
}
